import { useEffect, useState } from 'react';
import type {
  EinsatzAnsicht,
  TerminAntwort,
  UmsetzungAuftrag,
  UmsetzungListe,
} from '@arbeitsrapporte/shared';
import { useIch } from '../App';
import { api } from '../api';
import { Auftragswahl, Auftragszeile, type Auftragskopf } from '../bausteine/Auftragswahl';
import { Fehler } from '../bausteine/Meldung';
import { datumLesbar, heute } from '../format';
import { navigiere } from '../router';

/**
 * Die Disposition – wer wann zu wem fährt.
 *
 * **Die Oberfläche baut die Regeln nicht nach** (Regel 11): Welche Knöpfe
 * stehen, sagt `erlaubt` am Einsatz. Ob jemand planen darf, sagt seine
 * Rolle – und die kommt aus `GET /api/ich`, nicht aus dem Token im Browser.
 */

const ANTWORT_TEXT: Record<TerminAntwort, string> = {
  keine: 'niemand eingeladen',
  ausstehend: 'wartet auf Antwort',
  zugesagt: 'zugesagt',
  unter_vorbehalt: 'unter Vorbehalt',
  abgesagt: 'Kunde hat abgesagt',
};

/**
 * Die Antwort des Kunden als Marke – dieselben Töne wie am Rapportstatus:
 * bernstein heißt „wartet auf jemanden", grün „steht", rot „ist weg".
 */
const ANTWORT_TON: Record<TerminAntwort, string> = {
  keine: 'border-slate-300 bg-slate-100 text-slate-700',
  ausstehend: 'border-amber-300 bg-amber-50 text-amber-800',
  zugesagt: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  unter_vorbehalt: 'border-amber-300 bg-amber-50 text-amber-800',
  abgesagt: 'border-rose-300 bg-rose-50 text-rose-900',
};

/**
 * Welcher Zeitraum: Der Techniker schreibt den Rapport zu gestern und die
 * Leitung sucht den Einsatz von letzter Woche – „ab heute" allein ließ
 * beide im Stich.
 */
const ZEITRAEUME = ['kommende', 'letzte30', 'alle'] as const;
type Zeitraum = (typeof ZEITRAEUME)[number];

const ZEITRAUM_TEXT: Record<Zeitraum, string> = {
  kommende: 'Ab heute',
  letzte30: 'Letzte 30 Tage',
  alle: 'Alle',
};

function tageZurueck(tage: number): string {
  const d = new Date();

  d.setDate(d.getDate() - tage);

  return d.toISOString().slice(0, 10);
}

function Zusage({ einsatz, fertig }: { einsatz: EinsatzAnsicht; fertig: () => void }) {
  const [offen, setOffen] = useState(false);
  const [antwort, setAntwort] = useState<'zugesagt' | 'unter_vorbehalt' | 'abgesagt'>('zugesagt');
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const eintragen = async () => {
    setLaeuft(true);
    setFehler(null);

    try {
      await api.trageAntwortEin(einsatz.id, antwort, notiz);
      setOffen(false);
      setNotiz('');
      fertig();
    } catch (f) {
      setFehler(f);
    } finally {
      setLaeuft(false);
    }
  };

  if (!offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="min-h-0 px-0 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
      >
        Antwort von Hand eintragen
      </button>
    );
  }

  return (
    <div className="mt-2 w-full space-y-3 rounded border border-slate-200 bg-slate-50 p-3">
      <p className="text-sm text-slate-600">
        Für den Kunden, dessen Postfach keine maschinenlesbare Antwort schickt – oder der am Telefon
        zusagt. Die Notiz sagt später, woher die Zusage kam.
      </p>
      <select
        className="feld"
        aria-label="Antwort"
        value={antwort}
        onChange={(e) => setAntwort(e.target.value as typeof antwort)}
      >
        <option value="zugesagt">zugesagt</option>
        <option value="unter_vorbehalt">unter Vorbehalt</option>
        <option value="abgesagt">abgesagt</option>
      </select>
      <input
        className="feld"
        placeholder="Woher? z. B. „am Telefon, Frau Kunde“"
        value={notiz}
        onChange={(e) => setNotiz(e.target.value)}
      />
      <Fehler fehler={fehler} />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="knopf-haupt"
          disabled={laeuft || !notiz.trim()}
          onClick={eintragen}
        >
          Eintragen
        </button>
        <button type="button" className="knopf" onClick={() => setOffen(false)}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

function Absage({ einsatz, fertig }: { einsatz: EinsatzAnsicht; fertig: () => void }) {
  const [offen, setOffen] = useState(false);
  const [grund, setGrund] = useState('');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const absagen = async () => {
    setLaeuft(true);
    setFehler(null);

    try {
      await api.sageEinsatzAb(einsatz.id, grund);
      setOffen(false);
      fertig();
    } catch (f) {
      setFehler(f);
    } finally {
      setLaeuft(false);
    }
  };

  if (!offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="min-h-0 px-0 text-sm text-rose-800 underline underline-offset-4 hover:text-rose-900"
      >
        Absagen
      </button>
    );
  }

  return (
    <div className="mt-2 w-full space-y-3 rounded border border-rose-300 bg-rose-50 p-3">
      <p className="text-sm text-rose-900">
        Beide Termine werden entfernt; der Kunde bekommt die Absage von Outlook. Der Grund bleibt am
        Einsatz stehen.
      </p>
      <input
        className="feld"
        placeholder="Warum fällt er aus?"
        value={grund}
        onChange={(e) => setGrund(e.target.value)}
      />
      <Fehler fehler={fehler} />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="knopf-gefahr"
          disabled={laeuft || !grund.trim()}
          onClick={absagen}
        >
          Einsatz absagen
        </button>
        <button type="button" className="knopf" onClick={() => setOffen(false)}>
          Zurück
        </button>
      </div>
    </div>
  );
}

/**
 * Der Weg vom Einsatz zum Rapport – ein Griff.
 *
 * Auftrag, Ort, Ansprechpartner und Tätigkeit kommen mit; niemand sucht
 * den Auftrag unter „Neu" ein zweites Mal. Ist der Rapport schon da,
 * steht der Verweis dorthin.
 */
function RapportSchreiben({ einsatz }: { einsatz: EinsatzAnsicht }) {
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const schreiben = async () => {
    setLaeuft(true);
    setFehler(null);

    try {
      const r = await api.rapportAusEinsatz(einsatz.id, heute());

      navigiere(`/rapport/${r.id}/bearbeiten`);
    } catch (f) {
      setFehler(f);
      setLaeuft(false);
    }
  };

  return (
    <>
      <button type="button" className="knopf-haupt" disabled={laeuft} onClick={schreiben}>
        Rapport schreiben
      </button>
      <Fehler fehler={fehler} />
    </>
  );
}

function Karte({
  einsatz,
  fertig,
  onAendern,
}: {
  einsatz: EinsatzAnsicht;
  fertig: () => void;
  onAendern: () => void;
}) {
  return (
    <li className={`karte ${einsatz.status === 'abgesagt' ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3">
        <span className="font-semibold text-slate-900">
          {datumLesbar(einsatz.von)}
          {einsatz.bis !== einsatz.von ? ` – ${datumLesbar(einsatz.bis)}` : ''}
        </span>
        <span
          className={`ml-auto shrink-0 rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${ANTWORT_TON[einsatz.antwort]}`}
        >
          {ANTWORT_TEXT[einsatz.antwort]}
        </span>
      </div>

      <div className="mt-1 text-slate-900">
        <span className="font-medium">{einsatz.auftragNummer}</span> {einsatz.kundeName}
      </div>
      <div className="text-slate-700">{einsatz.taetigkeit || einsatz.auftragBezeichnung}</div>

      <div className="mt-1 text-sm text-slate-500">
        {einsatz.mitarbeiter.name}
        {einsatz.einsatzort ? ` · ${einsatz.einsatzort}` : ''}
      </div>

      {einsatz.ansprechpartnerName && (
        <div className="text-sm text-slate-500">
          Ansprechpartner: {einsatz.ansprechpartnerName}
          {einsatz.ansprechpartnerTelefon ? ` · ${einsatz.ansprechpartnerTelefon}` : ''}
          {einsatz.ansprechpartnerEmail ? ` · ${einsatz.ansprechpartnerEmail}` : ''}
        </div>
      )}

      {einsatz.antwortQuelle === 'hand' && (
        <div className="text-sm text-slate-500">{einsatz.antwortNotiz}</div>
      )}
      {einsatz.status === 'abgesagt' && (
        <div className="text-sm text-slate-500">Abgesagt: {einsatz.abgesagtGrund}</div>
      )}

      {/*
       * Eingeladen, aber kein Termin beim Techniker – oder umgekehrt: Das
       * passiert, wenn Graph beim Planen nicht erreichbar war. Es muss
       * dastehen, sonst hält jemand einen halben Einsatz für einen ganzen.
       */}
      {einsatz.status !== 'abgesagt' && !einsatz.terminGestellt && (
        <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Kein Termin im Kalender – Graph war nicht erreichbar.
        </p>
      )}

      {(einsatz.erlaubt.rapportSchreiben ||
        einsatz.rapportId ||
        einsatz.erlaubt.antwortEintragen ||
        einsatz.erlaubt.aendern ||
        einsatz.erlaubt.absagen) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 pt-3">
          {einsatz.erlaubt.rapportSchreiben && <RapportSchreiben einsatz={einsatz} />}
          {einsatz.rapportId && (
            <a
              href={`/rapport/${einsatz.rapportId}`}
              className="text-sm text-slate-700 underline underline-offset-4 hover:text-slate-900"
            >
              Zum Rapport
            </a>
          )}
          {einsatz.erlaubt.antwortEintragen && <Zusage einsatz={einsatz} fertig={fertig} />}
          {einsatz.erlaubt.aendern && (
            <button
              type="button"
              onClick={onAendern}
              className="min-h-0 px-0 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
            >
              Ändern
            </button>
          )}
          {einsatz.erlaubt.absagen && <Absage einsatz={einsatz} fertig={fertig} />}
        </div>
      )}
    </li>
  );
}

type Partner = { id: string; name: string; email: string; funktion: string };

/**
 * Planen und ändern – ein Formular für beides.
 *
 * Beim Ändern steht der Auftrag fest: Ein Einsatz, der zu einem anderen
 * Auftrag wandert, ist ein neuer Einsatz. Termin verschieben hieß vorher
 * absagen und neu planen – und der Kunde bekam zwei Mails.
 */
function Planen({
  vorhanden,
  vorgabe,
  fertig,
}: {
  vorhanden?: EinsatzAnsicht;
  /** Der Auftrag aus der Liste der freigegebenen – schon gewählt. */
  vorgabe?: Auftragskopf;
  fertig: () => void;
}) {
  const [auftrag, setAuftrag] = useState<Auftragskopf | null>(
    vorgabe ??
      (vorhanden
        ? {
            id: vorhanden.auftragId,
            gesellschaft: vorhanden.gesellschaft,
            nummer: vorhanden.auftragNummer,
            bezeichnung: vorhanden.auftragBezeichnung,
            kundeId: vorhanden.kundeId,
            kundeName: vorhanden.kundeName,
            kundeAnschrift: '',
            offen: true,
            /* Ein bestehender Einsatz steht auf einem gestellten Auftrag – sonst wäre er nie entstanden. */
            entwurf: false,
          }
        : null),
  );
  const [leute, setLeute] = useState<{ id: string; name: string }[]>([]);
  const [mitarbeiterId, setMitarbeiterId] = useState(vorhanden?.mitarbeiter.id ?? '');
  const [partner, setPartner] = useState<Partner[]>([]);
  const [ansprechpartnerId, setAnsprechpartnerId] = useState(vorhanden?.ansprechpartnerId ?? '');
  const [von, setVon] = useState(vorhanden?.von ?? heute());
  const [bis, setBis] = useState(vorhanden?.bis ?? heute());
  const [einsatzort, setEinsatzort] = useState(
    vorhanden?.einsatzort ?? vorgabe?.kundeAnschrift ?? '',
  );
  const [taetigkeit, setTaetigkeit] = useState(vorhanden?.taetigkeit ?? '');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    api.mitarbeiter().then(setLeute).catch(setFehler);
  }, []);

  /*
   * Die Ansprechpartner hängen am Kunden des gewählten Auftrags – erst wenn
   * er steht, gibt es etwas zu wählen. Ein Feld, das vorher schon dasteht,
   * wäre leer und sähe nach einem Fehler aus.
   */
  const kundeId = auftrag?.kundeId ?? null;

  useEffect(() => {
    if (!kundeId) {
      setPartner([]);

      return;
    }

    api.ansprechpartner(kundeId).then(setPartner).catch(setFehler);
  }, [kundeId]);

  const waehleAuftrag = (a: Auftragskopf) => {
    setAuftrag(a);
    setAnsprechpartnerId('');
    /* Die Anschrift des Kunden als Vorschlag – dorthin fährt man in aller Regel. */
    if (!einsatzort && a.kundeAnschrift) setEinsatzort(a.kundeAnschrift);
  };

  const absenden = async () => {
    if (!auftrag) return;

    setLaeuft(true);
    setFehler(null);

    try {
      if (vorhanden) {
        await api.aendereEinsatz(vorhanden.id, {
          mitarbeiterId,
          von,
          bis,
          einsatzort,
          taetigkeit,
          /*
           * Nur wenn ein anderer gewählt ist: Die eingefrorene Kopie bleibt
           * sonst, wie sie ist – die Einladung ist an diese Adresse gegangen.
           */
          ...((ansprechpartnerId || null) !== vorhanden.ansprechpartnerId
            ? { ansprechpartnerId: ansprechpartnerId || null }
            : {}),
        });
      } else {
        await api.planeEinsatz({
          auftragId: auftrag.id,
          mitarbeiterId,
          ansprechpartnerId: ansprechpartnerId || null,
          von,
          bis,
          einsatzort,
          taetigkeit,
          bemerkung: '',
        });
      }

      fertig();
    } catch (f) {
      setFehler(f);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void absenden();
      }}
      className="karte mt-3 space-y-4"
    >
      <h2 className="text-lg font-semibold">{vorhanden ? 'Einsatz ändern' : 'Einsatz planen'}</h2>

      <div>
        <span className="feldname">Auftrag</span>
        {auftrag ? (
          <div className="rounded border border-slate-200 bg-slate-50 p-3">
            <Auftragszeile auftrag={auftrag} />
            {!vorhanden && (
              <button
                type="button"
                className="mt-2 min-h-0 px-0 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
                onClick={() => setAuftrag(null)}
              >
                Anderen Auftrag wählen
              </button>
            )}
          </div>
        ) : (
          <Auftragswahl onWahl={waehleAuftrag} />
        )}
      </div>

      {auftrag && (
        <>
          <div>
            <label className="feldname" htmlFor="wer-faehrt">
              Wer fährt
            </label>
            <select
              id="wer-faehrt"
              className="feld"
              required
              value={mitarbeiterId}
              onChange={(e) => setMitarbeiterId(e.target.value)}
            >
              <option value="">Mitarbeiter wählen …</option>
              {leute.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
              {/*
               * Wer eingeteilt ist und seither keine Rolle mehr hat, steht
               * nicht mehr in der Liste – am bestehenden Einsatz bleibt er
               * trotzdem wählbar, sonst sähe das Feld leer aus und ein
               * Speichern verlöre ihn still.
               */}
              {vorhanden && !leute.some((m) => m.id === vorhanden.mitarbeiter.id) && (
                <option value={vorhanden.mitarbeiter.id}>
                  {vorhanden.mitarbeiter.name} (keine Rolle mehr)
                </option>
              )}
            </select>
          </div>

          <div>
            <label className="feldname" htmlFor="ansprechpartner">
              Ansprechpartner beim Kunden
            </label>
            <select
              id="ansprechpartner"
              className="feld"
              value={ansprechpartnerId}
              onChange={(e) => setAnsprechpartnerId(e.target.value)}
            >
              <option value="">niemand einladen</option>
              {/* Der bisherige bleibt wählbar, auch wenn er im Spiegel nicht mehr steht. */}
              {vorhanden?.ansprechpartnerId &&
                !partner.some((p) => p.id === vorhanden.ansprechpartnerId) && (
                  <option value={vorhanden.ansprechpartnerId}>
                    {vorhanden.ansprechpartnerName} – {vorhanden.ansprechpartnerEmail}
                  </option>
                )}
              {partner.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.email}>
                  {p.name}
                  {p.funktion ? ` (${p.funktion})` : ''}
                  {p.email ? ` – ${p.email}` : ' – keine Mailadresse im ERP'}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-slate-500">
              Aus dem ERP, nicht getippt. Wer gewählt ist, bekommt eine echte Einladung und kann
              zusagen; der Techniker bekommt den Termin ohne Rückfrage – er ist eingeteilt.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="feldname" htmlFor="einsatz-von">
                Von
              </label>
              <input
                id="einsatz-von"
                type="date"
                required
                className="feld"
                value={von}
                onChange={(e) => {
                  setVon(e.target.value);
                  /* Ein Tag ist der Regelfall – „bis" zieht mit, solange es davor läge. */
                  if (bis < e.target.value) setBis(e.target.value);
                }}
              />
            </div>
            <div>
              <label className="feldname" htmlFor="einsatz-bis">
                Bis
              </label>
              <input
                id="einsatz-bis"
                type="date"
                required
                min={von}
                className="feld"
                value={bis}
                onChange={(e) => setBis(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="feldname" htmlFor="einsatz-ort">
              Einsatzort
            </label>
            <input
              id="einsatz-ort"
              className="feld"
              maxLength={200}
              value={einsatzort}
              onChange={(e) => setEinsatzort(e.target.value)}
            />
          </div>

          <div>
            <label className="feldname" htmlFor="einsatz-taetigkeit">
              Tätigkeit
            </label>
            <input
              id="einsatz-taetigkeit"
              className="feld"
              maxLength={500}
              value={taetigkeit}
              onChange={(e) => setTaetigkeit(e.target.value)}
            />
          </div>
        </>
      )}

      <Fehler fehler={fehler} />

      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          className="knopf-haupt"
          disabled={laeuft || !auftrag || !mitarbeiterId || bis < von}
        >
          {vorhanden
            ? 'Änderung speichern und Termine nachziehen'
            : 'Einsatz planen und Termine stellen'}
        </button>
        <button type="button" className="knopf" disabled={laeuft} onClick={fertig}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

/**
 * Die Aufträge in der Umsetzung – worauf jemand eingeteilt werden muss.
 *
 * Freigegeben wird in der Auftragsabwicklung, wenn das Material vollständig
 * ist; hier wird gelesen, nicht geführt. Je Auftrag steht, wer schon
 * eingeplant ist – ein Auftrag ohne Einsatz ist der, um den es geht.
 */
function InUmsetzung({
  stand,
  onPlanen,
}: {
  stand: number;
  onPlanen: (auftrag: UmsetzungAuftrag) => void;
}) {
  const [liste, setListe] = useState<UmsetzungListe | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => {
    setFehler(null);
    api.umsetzung().then(setListe).catch(setFehler);
  }, [stand]);

  return (
    <section className="mt-6">
      <h2 className="mb-1 text-lg font-semibold">Freigegeben zur Umsetzung</h2>
      <p className="mb-3 text-sm text-slate-600">
        Aus der Auftragsabwicklung: Das Material ist vollständig, der Auftrag ist noch nicht
        abgeschlossen. Älteste Freigabe zuerst.
      </p>

      <Fehler fehler={fehler} />

      {liste === null && !fehler && <p className="text-slate-600">Lädt …</p>}
      {liste && !liste.eingerichtet && (
        <p className="text-sm text-slate-600">
          Die Auftragsabwicklung ist nicht angebunden – die Liste bleibt leer, bis sie eingerichtet
          ist.
        </p>
      )}
      {liste?.eingerichtet && liste.daten.length === 0 && (
        <p className="text-slate-600">Kein Auftrag wartet auf die Umsetzung.</p>
      )}
      {liste && liste.nichtImSpiegel > 0 && (
        <p className="mb-2 text-sm text-slate-500">
          {liste.nichtImSpiegel === 1
            ? '1 freigegebener Auftrag ist hier noch nicht bekannt'
            : `${liste.nichtImSpiegel} freigegebene Aufträge sind hier noch nicht bekannt`}{' '}
          – der nächste Abgleich mit dem ERP bringt ihn.
        </p>
      )}

      <ul className="space-y-2">
        {liste?.daten.map((a) => (
          <li key={a.id} className="karte">
            <Auftragszeile auftrag={a} />
            <div className="mt-1 text-sm text-slate-500">
              freigegeben am {datumLesbar(a.freigegebenAm.slice(0, 10))}
              {a.geliefert ? ' · Material geliefert' : ''}
            </div>

            {a.einsaetze.length ? (
              <ul className="mt-2 space-y-0.5 text-sm text-slate-700">
                {a.einsaetze.map((e) => (
                  <li key={e.id}>
                    {datumLesbar(e.von)}
                    {e.bis !== e.von ? ` – ${datumLesbar(e.bis)}` : ''} · {e.mitarbeiter}
                    {e.status === 'bestaetigt' ? ' · bestätigt' : ''}
                    {e.status === 'erledigt' ? ' · erledigt' : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-amber-800">Noch niemand eingeplant.</p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 pt-3">
              <button type="button" className="knopf-haupt" onClick={() => onPlanen(a)}>
                Einsatz planen
              </button>
              <a
                href={a.abwicklungWeg}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-slate-700 underline underline-offset-4 hover:text-slate-900"
              >
                In der Auftragsabwicklung
              </a>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Einsaetze() {
  const ich = useIch();
  const istLeitung = ich.rollen.includes('leitung');
  const [daten, setDaten] = useState<EinsatzAnsicht[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [zeitraum, setZeitraum] = useState<Zeitraum>('kommende');
  /**
   * `null`: nichts offen; `'neu'`: planen; `{ auftrag }`: planen auf einen
   * freigegebenen Auftrag; sonst der Einsatz, der geändert wird.
   */
  const [offen, setOffen] = useState<'neu' | { auftrag: UmsetzungAuftrag } | EinsatzAnsicht | null>(
    null,
  );
  /* Die Liste der freigegebenen lädt neu, wenn ein Einsatz dazukam. */
  const [stand, setStand] = useState(0);

  const laden = () => {
    setDaten(null);
    api
      .einsaetze(
        zeitraum === 'kommende'
          ? { von: heute() }
          : zeitraum === 'letzte30'
            ? { von: tageZurueck(30) }
            : {},
      )
      .then((a) =>
        /*
         * Kommende aufsteigend – das Nächste zuerst. Vergangene absteigend –
         * gestern zuerst, denn danach sucht, wer hier nachschlägt.
         */
        setDaten(zeitraum === 'kommende' ? a.daten : [...a.daten].reverse()),
      )
      .catch(setFehler);
  };

  useEffect(laden, [zeitraum]);

  const fertig = () => {
    setOffen(null);
    setStand((n) => n + 1);
    laden();
  };

  return (
    <>
      <h1 className="mb-1 text-2xl font-semibold">Einsätze</h1>
      <p className="mb-4 text-slate-600">
        Der Kunde bestätigt über die Einladung; die Zusage wird alle paar Minuten aus dem Kalender
        geholt.
      </p>

      {istLeitung && offen === null && (
        <button type="button" className="knopf-haupt" onClick={() => setOffen('neu')}>
          Einsatz planen
        </button>
      )}
      {offen === 'neu' && <Planen fertig={fertig} />}
      {offen !== null && offen !== 'neu' && 'auftrag' in offen && (
        <Planen vorgabe={offen.auftrag} fertig={fertig} />
      )}
      {offen !== null && offen !== 'neu' && !('auftrag' in offen) && (
        <Planen vorhanden={offen} fertig={fertig} />
      )}

      {istLeitung && offen === null && (
        <InUmsetzung stand={stand} onPlanen={(auftrag) => setOffen({ auftrag })} />
      )}

      <div className="mt-6">
        <div className="mb-4 flex flex-wrap gap-2">
          {ZEITRAEUME.map((z) => (
            <button
              key={z}
              type="button"
              aria-pressed={zeitraum === z}
              onClick={() => setZeitraum(z)}
              className={`min-h-0 rounded-full border px-3 py-1.5 text-sm font-medium ${
                zeitraum === z
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {ZEITRAUM_TEXT[z]}
            </button>
          ))}
        </div>

        <Fehler fehler={fehler} />

        {daten === null && !fehler && <p className="text-slate-600">Lädt …</p>}
        {daten?.length === 0 && (
          <p className="text-slate-600">
            {zeitraum === 'kommende' ? 'Nichts geplant.' : 'Kein Einsatz in diesem Zeitraum.'}
          </p>
        )}

        <ul className="space-y-2">
          {daten?.map((e) => (
            <Karte key={e.id} einsatz={e} fertig={laden} onAendern={() => setOffen(e)} />
          ))}
        </ul>
      </div>
    </>
  );
}
